import {DynamicPageNavBarActionId, EDITOR_ECOM_SCRIPT_DSN, Events, FedopsInteractions, SPECS} from './constants';
import {withMembersArea} from '@wix/members-area-integration-kit';
import webLogger from '@wix/web-bi-logger';
import {Logger as WebBiLogger} from '@wix/web-bi-logger/dist/src/types';
import {getAppManifest} from '../common/appManifest/appManifest';
import {DependantApps} from './services/DependantApps';
import {StyleParams} from './services/StyleParams';
import {translateFunctionFactory} from '../common/translations/translations';
import {
  errorReporter,
  setSentryInstance,
  withErrorReportingWrapping,
} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/errorReporter';
import {
  addLightbox,
  addMembersArea,
  addPage,
  addRouter,
  addWidget,
  connectPageToRouter,
  getEcomPlatformAppManifest,
  getRouter,
  getRouterRefByPrefix,
  installWishlistApp,
  openEcomBillingPage,
  setStateForPages,
  setStateForStoresPages,
  setThankYouPageStyleParams,
  toMonitored,
  updateEcomPagesForPagesPanel,
  updateEcomPlatformInstallationsData,
  disablePopupAutoOpen,
  onPageDeleted,
  onComponentSettingsPanelClosed,
  setStyleParams,
  refreshRouters,
  setWidgetViewState,
} from './editor-script-partials/publicApi';
import {handleOnEvent} from './editor-script-partials/onEvent';
import {doTransaction} from './transaction';
import {handleAction} from './editor-script-partials/handleAction';
import {createAppApi} from './editor-script-partials/appApi';
import {ExperimentsApi} from '../common/experiments/ExperimentsApi';
import {AppManifestBuilder} from '@wix/app-manifest-builder';
import {ContextParams, EditorSDK, PageRef, RouterRef} from '@wix/platform-editor-sdk';
import {EcomComponentsData, EcomPlatformInstallationState} from './services/EcomPlatformInstallationState';
import {createSentryInstance} from '../common/monitoring';
import {EcomStyleParams} from '@wix/ecom-platform-sdk/dist/es/src/types';
import {AppApiModel} from '@wix/wixstores-client-core/dist/src/types/app-api-model';
import {type AnyObject, createOOIWebBILogger} from '@wix/wixstores-client-core';
import {IInstallReviewsPluginProps, installReviewsPlugin} from './editor-script-partials/reviews';
import {WIX_ECOM} from '@wix/app-definition-ids';
import {PageMap} from '@wix/wixstores-client-core/dist/es/src/constants';
import {ComponentRef} from '@wix/editor-platform-sdk-types/dist/types/common';
import {StyleParam} from '@wix/editor-platform-sdk-types/dist/types/tpaStyleParams';

let appToken;
let options: ContextParams;
let t: (keyToTranslate: string, values?: AnyObject) => string;
let locale: string = 'en';
let webBiLogger: WebBiLogger;
let sdk: EditorSDK;
let experimentsApi: ExperimentsApi;
let dependantApps: DependantApps;
let appApi: AppApiModel;
let ecomPlatformInstallationState;
let styleParams: StyleParams;
let storeId: string;
let fedopsLogger: Record<string, any>;

function registerEvents() {
  return sdk.document.application.registerToCustomEvents('', {eventTypes: [Events.dashboardClosed]});
}

async function editorReady(_editorSDK: EditorSDK, _appToken, _options) {
  options = _options;
  appToken = _appToken;
  sdk = _editorSDK;

  fedopsLogger = options?.monitoring?.createFedopsLogger()('ecom-platform-editor-script');
  const instance: string = options.initialAppData.instance;
  experimentsApi = new ExperimentsApi(instance);
  await toMonitored(fedopsLogger, FedopsInteractions.EcomInstallationFetchExperiments, () => experimentsApi.fetch());
  ecomPlatformInstallationState = new EcomPlatformInstallationState();
  try {
    if (options.firstInstall) {
      fedopsLogger?.interactionStarted?.(FedopsInteractions.EcomPlatformInstallationEditorReady);
      fedopsLogger?.interactionStarted?.(FedopsInteractions.EcomPlatformInstallationAppInstalled);
    }

    setSentryInstance(createSentryInstance(options.monitoring, EDITOR_ECOM_SCRIPT_DSN));
    await toMonitored(fedopsLogger, FedopsInteractions.EcomInstallationRegisterEvents, () => registerEvents());

    const encodedInstance = instance.substring(instance.indexOf('.') + 1);
    const parsedInstance = JSON.parse(atob(encodedInstance));
    storeId = parsedInstance.instanceId;
    const isMerchant = true;

    const user = {uuid: parsedInstance.uid};
    webBiLogger = createOOIWebBILogger({
      user,
      biLoggerFactory: webLogger.factory,
      biToken: parsedInstance.biToken,
      defaults: {
        storeId,
        isMerchant,
        appName: 'wixstores worker',
      },
    });

    dependantApps = new DependantApps(sdk, instance, options.initialAppData.metaSiteId, webBiLogger);
    styleParams = new StyleParams(sdk, options.initialAppData.applicationId);
    locale =
      (await toMonitored(fedopsLogger, FedopsInteractions.EcomInstallationGetLocale, () =>
        sdk.editor.environment.getLocale()
      )) || locale;
    t = await toMonitored(fedopsLogger, FedopsInteractions.EcomInstallationCreateTFunctionFactory, () =>
      translateFunctionFactory(locale)
    );
    appApi = createAppApi({sdk, dependantApps, styleParams, t, webBiLogger});
    await sdk.editor.setAppAPI('', withErrorReportingWrapping(appApi));
    await doTransaction(sdk, async () => {
      await toMonitored(fedopsLogger, FedopsInteractions.EcomInstallationInstallSubscriptionsPageInMembersArea, () =>
        dependantApps.installMySubscriptionsPageInMembersAreaIfNeeded(sdk, {biData: options.biData})
      );
    });

    await disablePopupAutoOpen(sdk, PageMap.SIDE_CART);

    await registerToEditorEvents();

    if (options.firstInstall) {
      fedopsLogger?.interactionEnded?.(FedopsInteractions.EcomPlatformInstallationEditorReady);
    }
  } catch (err) {
    fedopsLogger?.interactionFailed?.(FedopsInteractions.EcomPlatformInstallationEditorReady, err as Error);
  }
}

async function registerToEditorEvents() {
  await sdk.addEventListener('navBarMainActionClicked', (event) => {
    const {actionId} = event.detail;
    if (actionId === DynamicPageNavBarActionId.openProductListDashboard) {
      void sdk.editor.openDashboardPanel('', {url: 'products', closeOtherPanels: false});
    } else if (actionId === DynamicPageNavBarActionId.openCategoryListDashboard) {
      void sdk.editor.openDashboardPanel('', {url: 'wix-stores/categories/list', closeOtherPanels: false});
    }
  });

  if (experimentsApi.experiments.enabled(SPECS.ListenToSwitchedFromPreviewEventOnEditor)) {
    await sdk.addEventListener('switchedFromPreview', () => {
      void (async () => {
        const currentPageRef = await sdk.pages.getCurrent('');
        const currentPageData = await sdk.pages.getPageData('', {pageRef: currentPageRef});
        if (currentPageData?.tpaPageId === PageMap.CHECKOUT) {
          const homePage = await sdk.pages.getHomePage('');
          return sdk.pages.navigateTo('', {pageRef: homePage});
        }
      })();
    });
  }
}

export function getPublicApi(editorSDK: EditorSDK, contextParams: ContextParams) {
  return {
    addPage: (pageId: string, managingAppDefId = WIX_ECOM, shouldNavigate = false) =>
      addPage({
        sdk: editorSDK,
        pageId,
        managingAppDefId,
        shouldNavigate,
        fedopsLogger,
        reportError: errorReporter,
      }),
    addWidget: (widgetId: string, addToAllPages: boolean) =>
      addWidget(editorSDK, widgetId, addToAllPages, fedopsLogger, errorReporter),
    addLightbox: (lightboxId: string, managingAppDefId = WIX_ECOM) =>
      addLightbox({
        sdk: editorSDK,
        lightboxId,
        managingAppDefId,
        fedopsLogger,
        reportError: errorReporter,
        contextParams,
        t,
        experiments: experimentsApi.experiments,
      }),
    getRouterRefByPrefix: (prefix: string) => getRouterRefByPrefix(editorSDK, prefix),
    addRouter: (prefix: string) => addRouter(editorSDK, prefix, errorReporter),
    getRouter: (routerRef: RouterRef) => getRouter(editorSDK, routerRef),
    connectPageToRouter: (connectPageOptions: {pageRef: PageRef; routerRef: RouterRef; pageRoles: string[]}) =>
      connectPageToRouter(editorSDK, connectPageOptions, errorReporter),
    refreshRouters: () => refreshRouters(editorSDK),
    addMembersArea: (
      editorType: string,
      firstInstall: boolean,
      biData?: {origin?: string},
      siteCreationFlow: boolean = false
    ) => addMembersArea(editorSDK, editorType, firstInstall, biData, siteCreationFlow, fedopsLogger),
    setStateForStoresPages: () => setStateForStoresPages(editorSDK),
    setStateForPages: () => setStateForPages(editorSDK),
    openEcomBillingPage: () => openEcomBillingPage(editorSDK),
    updateEcomPlatformInstallationsData: (appDefId: string, ecomComponentsData: EcomComponentsData) =>
      updateEcomPlatformInstallationsData(ecomPlatformInstallationState, appDefId, ecomComponentsData),
    updateEcomPagesForPagesPanel: () => updateEcomPagesForPagesPanel(editorSDK, ecomPlatformInstallationState),
    getEcomPlatformAppManifest: () => getEcomPlatformAppManifest(t, locale),
    getInstance: () => contextParams.initialAppData.instance,
    setThankYouPageStyleParams: (thankYouPageStyleParams: EcomStyleParams) =>
      setThankYouPageStyleParams({
        sdk: editorSDK,
        ecomPlatformInstallationState,
        styleParams: thankYouPageStyleParams,
        experimentsApi,
      }),
    installWishlistApp: () => installWishlistApp({sdk: editorSDK, dependantApps, t, webBiLogger}),
    installReviewsPlugin: ({targetSlot, widgetPluginPointer}: IInstallReviewsPluginProps) =>
      installReviewsPlugin({editorSDK, targetSlot, widgetPluginPointer}),
    onPageDeleted: (callback: (event: CustomEvent<{pageRole: string}>) => any) => onPageDeleted(editorSDK, callback),
    onComponentSettingsPanelClosed: (callback: (event: CustomEvent<{componentRef: ComponentRef}>) => any) =>
      onComponentSettingsPanelClosed(editorSDK, callback),
    setWidgetViewState: (componentRef: ComponentRef, viewState: string) =>
      setWidgetViewState(editorSDK, componentRef, viewState),
    setStyleParams: (compRef: ComponentRef, styleParamsToSet: StyleParam[]) =>
      setStyleParams(editorSDK, compRef, styleParamsToSet),
  };
}

export async function getDynamicPageNavBarConfig(editorSDK: EditorSDK, pageRef: PageRef, numberOfItems: number) {
  const currentPage = await editorSDK.pages.data.get('', {pageRef});
  const isCategoryPage = currentPage?.tpaPageId?.startsWith(PageMap.CATEGORY);
  const isProductPage = currentPage?.tpaPageId?.startsWith(PageMap.PRODUCT);

  if (isProductPage) {
    return {
      navBarMainAction: {
        label: t('productPage.sausage.manageProducts.cta'),
        actionId: DynamicPageNavBarActionId.openProductListDashboard,
      },
      numberAndLabel:
        numberOfItems > 1
          ? t('productPage.sausage.itemCount.plural.label', {productCount: numberOfItems})
          : t('productPage.sausage.itemCount.one.label'),
    };
  } else if (isCategoryPage) {
    return {
      navBarMainAction: {
        label: t('gallery.sausage.manageCategories.cta'),
        actionId: DynamicPageNavBarActionId.openCategoryListDashboard,
      },
      numberAndLabel:
        numberOfItems > 1
          ? t('gallery.sausage.itemCount.plural.label', {catCount: numberOfItems})
          : t('gallery.sausage.itemCount.one.label'),
    };
  }
}

export const editorScript = withMembersArea(
  withErrorReportingWrapping({
    editorReady,
    handleAction: (data, editorSdk) =>
      toMonitored(fedopsLogger, FedopsInteractions.EcomHandleAction, () =>
        handleAction(data, {sdk: editorSdk, dependantApps, ecomPlatformInstallationState, webBiLogger, fedopsLogger})
      ),
    getAppManifest: ({appManifestBuilder}: {appManifestBuilder: AppManifestBuilder}, editorSDK: EditorSDK) =>
      getAppManifest({
        t,
        locale,
        token: appToken,
        hasStoresPremium: experimentsApi.hasStoresPremium,
        exps: experimentsApi.experiments,
        appManifestBuilder,
        editorSDK,
        options,
        fedopsLogger,
        pagesPanelTitle: t('Pages_Panel_ecomPlatformPages'),
      }),
    onEvent: (data, editorSDK) =>
      toMonitored(fedopsLogger, FedopsInteractions.EcomOnEvent, () => handleOnEvent(data, editorSDK, dependantApps)),
    getControllerPresets: () => Promise.resolve([]),
    exports: (editorSDK: EditorSDK, contextParams: ContextParams) => ({
      public: getPublicApi(editorSDK, contextParams),
      editor: {
        getDynamicPageNavBarConfig: (data: {pageRef: PageRef; numberOfItems: number}) =>
          getDynamicPageNavBarConfig(editorSDK, data.pageRef, data.numberOfItems),
      },
    }),
  }),
  {installAutomatically: false}
);

export type EcomPublicApi = ReturnType<typeof getPublicApi>;
